import type { AdminGymFoodReport, GymSearchKind } from '@steado/admin-contract/gym-catalog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { DataTable, type TableColumn, type TableRow } from '@/components/table/DataTable';
import { ScopePills } from '@/components/table/ScopePills';
import { BadgeCell, RowActionsCell, TextCell } from '@/components/table/cells';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth/auth-context';
import { formatDateTime, formatRelative } from '@/lib/format';

import {
  answerGymReport,
  GYM_CATALOG_KEY,
  gymReportsQueryOptions,
  gymSearchMissesQueryOptions,
} from './gym-catalog-query';

const REPORT_COLUMNS: readonly TableColumn[] = [
  { key: 'food', label: 'Catalog food', width: '1.1fr' },
  { key: 'says', label: 'What the gym says', width: '1.8fr' },
  { key: 'proposed', label: 'Proposed per 100 g (kcal · P · C · F)', width: '1.2fr' },
  { key: 'who', label: 'From', width: '1fr' },
  { key: 'actions', label: '', width: '220px', align: 'right' },
];

const STATUS_LABELS: Record<AdminGymFoodReport['status'], string> = {
  open: 'Open',
  resolved: 'Fixed',
  rejected: 'Left as it is',
};

export interface GymReportsProps {
  readonly status: AdminGymFoodReport['status'];
  readonly onStatusChange: (status: AdminGymFoodReport['status']) => void;
  /** Opens the catalog food's own page, where the fix is made. */
  readonly onOpenFood: (foodId: string) => void;
}

/**
 * What gyms say is wrong with catalog foods. The fix is made on the food's own
 * page; answering here closes the report and tells the gym's trainer, in the
 * answer's words.
 */
export function GymReports({ status, onStatusChange, onOpenFood }: GymReportsProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const answerId = useId();
  const reports = useQuery(gymReportsQueryOptions(api, status));
  const [answering, setAnswering] = useState<{
    report: AdminGymFoodReport;
    verdict: 'resolve' | 'reject';
  } | null>(null);
  const [words, setWords] = useState('');

  const answer = useMutation({
    mutationFn: () =>
      answerGymReport(api, answering?.report.id ?? '', answering?.verdict ?? 'resolve', words.trim()),
    onSuccess: () => {
      setAnswering(null);
      setWords('');
      void queryClient.invalidateQueries({ queryKey: GYM_CATALOG_KEY });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'scope-counts'] });
    },
    retry: false,
  });

  const rows: readonly TableRow[] = (reports.data ?? []).map((report) => {
    const p = report.proposed;
    const f = report.food;
    return {
      id: report.id,
      cells: [
        <TextCell
          key="food"
          value={f.name}
          sub={`now ${String(Math.round(f.kcalPer100g))} · ${String(f.proteinGPer100g)} · ${String(f.carbsGPer100g)} · ${String(f.fatGPer100g)}`}
          weight="medium"
        />,
        <span key="says" className="flex flex-col gap-1">
          <BadgeCell label={report.kind} tone="mute" />
          <span className="text-[13px] text-foreground">{report.note ?? ''}</span>
          {report.source === null ? null : (
            <span className="text-xs text-muted-foreground">Source: {report.source}</span>
          )}
          {report.resolution === null ? null : (
            <span className="text-xs text-muted-foreground">Answer: {report.resolution}</span>
          )}
        </span>,
        <TextCell
          key="proposed"
          mono
          value={
            p === null
              ? '—'
              : `${String(p.kcal)} · ${String(p.proteinG)} · ${String(p.carbsG)} · ${String(p.fatG)}`
          }
        />,
        <TextCell
          key="who"
          value={report.gymName}
          sub={`${report.reportedByName ?? 'A trainer'} · ${formatRelative(report.createdAt)}`}
        />,
        <RowActionsCell key="actions">
          <Button
            variant="ghost"
            size="xs"
            onClick={() => {
              onOpenFood(f.id);
            }}
          >
            Open food
          </Button>
          {report.status === 'open' && can.mutate ? (
            <>
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  setAnswering({ report, verdict: 'resolve' });
                }}
              >
                Fixed
              </Button>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  setAnswering({ report, verdict: 'reject' });
                }}
              >
                Leave it
              </Button>
            </>
          ) : null}
        </RowActionsCell>,
      ],
    };
  });

  return (
    <div className="flex flex-col gap-3">
      <ScopePills
        label="Report status"
        scopes={(['open', 'resolved', 'rejected'] as const).map((id) => ({ id, label: STATUS_LABELS[id] }))}
        value={status}
        onChange={onStatusChange}
      />
      {reports.isError ? (
        <Alert role="alert" tone="err">
          <AlertDescription>{reports.error.message}</AlertDescription>
        </Alert>
      ) : null}
      <DataTable
        caption="Reports from gyms"
        title={STATUS_LABELS[status]}
        columns={REPORT_COLUMNS}
        rows={rows}
        isLoading={reports.isPending}
        emptyTitle="Nothing here"
        emptyBody="No report from a gym in this scope."
      />
      {answering !== null ? (
        <ConfirmDialog
          title={answering.verdict === 'resolve' ? 'Mark it fixed?' : 'Leave it as it is?'}
          body={
            <div className="flex flex-col gap-3">
              <p>
                {answering.verdict === 'resolve'
                  ? `Fix ${answering.report.food.name} on its own page first. This closes the report and tells ${answering.report.gymName}’s trainer.`
                  : `This closes the report without a change and tells ${answering.report.gymName}’s trainer why.`}
              </p>
              <div className="flex flex-col gap-2">
                <Label htmlFor={answerId} className="text-xs font-medium text-muted-foreground">
                  Answer, as the trainer will read it
                </Label>
                <Input
                  id={answerId}
                  value={words}
                  maxLength={500}
                  onChange={(event) => {
                    setWords(event.target.value);
                  }}
                  placeholder={
                    answering.verdict === 'resolve'
                      ? 'Fixed to the label: 18 g protein'
                      : 'IFCT measures it raw'
                  }
                />
              </div>
            </div>
          }
          confirmLabel={answering.verdict === 'resolve' ? 'Mark fixed' : 'Leave it'}
          confirmDisabled={words.trim() === ''}
          onConfirm={() => {
            answer.mutate();
          }}
          onCancel={() => {
            setAnswering(null);
            setWords('');
            answer.reset();
          }}
          isPending={answer.isPending}
          error={answer.isError ? answer.error.message : undefined}
        />
      ) : null}
    </div>
  );
}

const MISS_COLUMNS: readonly TableColumn[] = [
  { key: 'query', label: 'Searched for', width: '1.6fr' },
  { key: 'gyms', label: 'Gyms', width: '0.6fr', align: 'right' },
  { key: 'count', label: 'Times', width: '0.6fr', align: 'right' },
  { key: 'last', label: 'Last', width: '1fr' },
];

export interface GymSearchMissesProps {
  readonly kind: GymSearchKind;
  readonly onKindChange: (kind: GymSearchKind) => void;
}

/** What trainers searched for and found nowhere: the gaps, most gyms first. */
export function GymSearchMisses({ kind, onKindChange }: GymSearchMissesProps) {
  const { api } = useAuth();
  const misses = useQuery(gymSearchMissesQueryOptions(api, kind));
  const rows: readonly TableRow[] = (misses.data ?? []).map((miss) => ({
    id: `${kind}:${miss.query}`,
    cells: [
      <TextCell key="query" value={miss.query} weight="medium" />,
      <TextCell key="gyms" value={String(miss.gyms)} align="right" />,
      <TextCell key="count" value={String(miss.count)} align="right" />,
      <TextCell key="last" value={formatRelative(miss.lastAt)} sub={formatDateTime(miss.lastAt)} />,
    ],
  }));
  return (
    <div className="flex flex-col gap-3">
      <ScopePills
        label="Searched for"
        scopes={[
          { id: 'food' as const, label: 'Foods' },
          { id: 'exercise' as const, label: 'Exercises' },
        ]}
        value={kind}
        onChange={onKindChange}
      />
      {misses.isError ? (
        <Alert role="alert" tone="err">
          <AlertDescription>{misses.error.message}</AlertDescription>
        </Alert>
      ) : null}
      <DataTable
        caption="Searches that found nothing"
        title={kind === 'food' ? 'Foods trainers could not find' : 'Exercises trainers could not find'}
        columns={MISS_COLUMNS}
        rows={rows}
        isLoading={misses.isPending}
        emptyTitle="No misses"
        emptyBody="Every search a trainer ran found something."
      />
    </div>
  );
}
